import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { VaultBaseComponent } from '@app/shared/components/base/vault-base.component';
import { setIsLoading } from '@app/state/settings/settings.actions';
import { DateFormatterService } from '@core/services/helpers/date-formatter.service';
import { FormErrorHandlerService } from '@core/services/helpers/form-error-handler.service';
import { ConsolidatedDueDateHttpService } from '@core/services/http/consolidated-due-date/consolidated-due-date-http.service';
import { Option } from '@interfaces/common/option';
import { ConsolidatedDueDateResponse } from '@interfaces/consolidated-due-date/consolidated-due-date.response';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { CompanyService } from '@services/http/company.service';
import { TableExportService } from '@shared/components/table/services/table-export.service';
import { ColumnDefinition } from '@shared/interfaces/column-definition.interface';
import { SharedModule } from '@shared/shared.module';
import { PdfUtilsServiceV2 } from '@src/app/core/services/helpers/pdf-utils/pdf-utils-v2.service';
import ModalComexOperationDetailsComponent from '@src/app/features/dashboard/comex/pages/modal-comex-operation-details/modal-comex-operation-details.component';
import { ModalLoanDetailsComponent } from '@src/app/features/dashboard/costs/debt-management/pages/modal-loan-details/modal-loan-details.component';
import { SortEvent } from '@src/app/shared/components/table/table.component';
import { AppState } from '@src/app/state';
import { selectCompanyId } from '@src/app/state/auth/auth.selectors';
import {
  addDays,
  addMonths,
  addWeeks,
  endOfMonth,
  endOfWeek,
  startOfMonth,
  startOfToday,
  startOfWeek,
} from 'date-fns';
import { MenuItem } from 'primeng/api';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { debounceTime, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-consolidated-due-date',
  templateUrl: './consolidated-due-date.component.html',
  styleUrls: ['./consolidated-due-date.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SharedModule,
    ProgressSpinnerModule,
    ModalLoanDetailsComponent,
    ModalComexOperationDetailsComponent,
  ],
})
export class ConsolidatedDueDateComponent extends VaultBaseComponent implements OnInit, OnDestroy {
  @ViewChild('loanDetailsModal') loanDetailsModal!: ModalLoanDetailsComponent;
  @ViewChild('comexDetailsModal') comexDetailsModal!: ModalComexOperationDetailsComponent;

  private destroy$ = new Subject<void>();
  private filterSubject$ = new Subject<void>();

  home: MenuItem = {
    icon: 'pi pi-home',
    routerLink: '/dashboard/dashboard-management',
    label: 'Cognito',
  };
  breadcrumbs: MenuItem[] = [
    {
      routerLink: '/dashboard/dashboard-management',
      label: 'Dashboard',
    },
    {
      label: 'Próximos Vencimentos',
    },
  ];

  companiesOptions: Option[] = [];
  selectedCompanyId: string | null = null;
  isCompanyIdUser = false;

  exportMenuItems: MenuItem[] = [
    {
      label: 'Exportar PDF',
      icon: 'pi pi-file-pdf',
      command: () => this.exportToPdf(),
    },
    {
      label: 'Exportar Excel',
      icon: 'pi pi-file-excel',
      command: () => this.exportToExcel(),
    },
  ];

  periodOptions: Option[] = [
    { label: 'Hoje', value: 'today' },
    { label: 'Esta semana', value: 'thisWeek' },
    { label: 'Próxima Semana', value: 'nextWeek' },
    { label: 'Este mês', value: 'thisMonth' },
    { label: 'Próximo mês', value: 'nextMonth' },
    { label: 'Próximos 7 dias', value: 'next7Days' },
    { label: 'Próximos 15 dias', value: 'next15Days' },
    { label: 'Próximos 90 dias', value: 'next90Days' },
    { label: 'Personalizado', value: 'custom' },
  ];
  selectedPeriod: string = 'thisMonth';

  startDate: Date = new Date();
  endDate: Date = new Date();

  data: ConsolidatedDueDateResponse | null = null;

  derivativeColumns: ColumnDefinition[] = [];
  loanColumns: ColumnDefinition[] = [];
  comexColumns: ColumnDefinition[] = [];

  kpiGroups: {
    label: string;
    icon: string;
    items: { value: number; currency: string; count: number }[];
    totalBRL: number;
    isGeneral?: boolean;
  }[] = [];
  kpiLoading: boolean = false;

  constructor(
    translateService: TranslateService,
    protected override store: Store<AppState>,
    public dateFormatter: DateFormatterService,
    private companyService: CompanyService,
    private consolidatedHttpService: ConsolidatedDueDateHttpService,
    private formErrorHandlerService: FormErrorHandlerService,
    private route: ActivatedRoute,
    private router: Router,
    private pdfUtilsService: PdfUtilsServiceV2,
    private tableExportService: TableExportService
  ) {
    super(translateService, store);
  }

  ngOnInit(): void {
    this.initColumns();
    this.getCompanyOptions();
    this.initFromQueryParams();

    this.store
      .select(selectCompanyId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(companyId => {
        if (companyId && !this.selectedCompanyId) {
          this.selectedCompanyId = companyId;
          this.isCompanyIdUser = true;
          this.handleFilterChange();
        }
      });

    this.filterSubject$.pipe(debounceTime(500), takeUntil(this.destroy$)).subscribe(() => {
      this.updateUrlAndFetch();
    });

    // If we have everything needed, trigger a fetch.
    // The debounce will handle merging this with any store update happening at the same time.
    this.handleFilterChange();
  }

  private initFromQueryParams(): void {
    const params = this.route.snapshot.queryParams;

    if (params['startDate']) {
      const parsed = this.dateFormatter.parseDate(params['startDate']);
      if (parsed) this.startDate = parsed;
    }
    if (params['endDate']) {
      const parsed = this.dateFormatter.parseDate(params['endDate']);
      if (parsed) this.endDate = parsed;
    }
    if (params['companyId']) {
      this.selectedCompanyId = params['companyId'];
    }
    if (params['period']) {
      this.selectedPeriod = params['period'];
    }

    // If no dates in URL, apply default period
    if (!params['startDate'] && !params['endDate']) {
      this.handlePeriodChange(false);
    }
  }

  private updateUrlAndFetch(): void {
    const queryParams = {
      startDate: this.dateFormatter.formatDateToYYYYMMDD(this.startDate),
      endDate: this.dateFormatter.formatDateToYYYYMMDD(this.endDate),
      companyId: this.selectedCompanyId || null,
      period: this.selectedPeriod,
    };

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });

    this.initColumns();
    this.fetchData();
  }

  initColumns(): void {
    const showCompanyColumn = !this.selectedCompanyId;

    this.derivativeColumns = [
      new ColumnDefinition({ field: 'sequentialId', header: 'ID' }),
      ...(showCompanyColumn
        ? [new ColumnDefinition({ field: 'companyName', header: 'Empresa' })]
        : []),
      new ColumnDefinition({ field: 'demandReference', header: 'Referência' }),
      new ColumnDefinition({ field: 'dueDate', header: 'Vencimento' }),
      new ColumnDefinition({ field: 'strategy', header: 'Estratégia' }),
      new ColumnDefinition({ field: 'notionalAmount', header: 'Valor Notional' }),
      new ColumnDefinition({ field: 'netAmount', header: 'Ajuste' }),
      new ColumnDefinition({ field: 'incomeTax', header: 'IR' }),
    ];

    this.loanColumns = [
      new ColumnDefinition({ field: 'sequentialId', header: 'ID' }),
      ...(showCompanyColumn
        ? [new ColumnDefinition({ field: 'companyName', header: 'Empresa' })]
        : []),
      new ColumnDefinition({ field: 'loanReference', header: 'Referência' }),
      new ColumnDefinition({ field: 'dueDate', header: 'Vencimento' }),
      new ColumnDefinition({ field: 'installmentNumber', header: 'Parcela' }),
      new ColumnDefinition({ field: 'installmentAmount', header: 'Valor' }),
      new ColumnDefinition({ field: 'estimatedAmountBRL', header: 'Previsto (BRL)' }),
    ];

    // Larguras fixas nas colunas curtas: a Referência fica com o espaço que sobra
    this.comexColumns = [
      new ColumnDefinition({ field: 'sequentialId', header: 'ID', style: { width: '8rem' } }),
      ...(showCompanyColumn
        ? [
            new ColumnDefinition({
              field: 'companyName',
              header: 'Empresa',
              style: { width: '9.5rem' },
            }),
          ]
        : []),
      new ColumnDefinition({
        field: 'comexReference',
        header: 'Referência',
        style: { minWidth: '14rem' },
      }),
      new ColumnDefinition({
        field: 'expirationDate',
        header: 'Data Vencimento da LC',
        style: { width: '13rem' },
      }),
      new ColumnDefinition({
        field: 'lcPaymentDate',
        header: 'Data Pagamento LC',
        style: { width: '11.5rem' },
      }),
      new ColumnDefinition({ field: 'amount', header: 'Valor', style: { width: '9.5rem' } }),
      new ColumnDefinition({
        field: 'estimatedAmountBRL',
        header: 'Previsto (BRL)',
        style: { width: '10.5rem' },
      }),
    ];
  }

  getCompanyOptions(): void {
    this.companyService.getAllCompanys().subscribe(response => {
      this.companiesOptions = (response?.data || []).map(c => ({
        label: c.displayName || '',
        value: c.id?.toString() || '',
      }));
    });
  }

  handleFilterChange(): void {
    this.filterSubject$.next();
  }

  openLoanDetails(id: string): void {
    this.loanDetailsModal.open(id);
  }

  openComexDetails(id: string): void {
    this.comexDetailsModal.open(id);
  }

  getDerivativesTotalNetAmount(): number {
    return this.data?.derivatives?.reduce((acc, curr) => acc + (curr.netAmount || 0), 0) || 0;
  }

  getDerivativesTotalIncomeTax(): number {
    return this.data?.derivatives?.reduce((acc, curr) => acc + (curr.incomeTax || 0), 0) || 0;
  }

  getLoansTotalEstimatedBRL(): number {
    return this.data?.loans?.reduce((acc, curr) => acc + (curr.estimatedAmountBRL || 0), 0) || 0;
  }

  getComexTotalEstimatedBRL(): number {
    return this.data?.comex?.reduce((acc, curr) => acc + (curr.estimatedAmountBRL || 0), 0) || 0;
  }

  handleSort(event: SortEvent, type: 'derivatives' | 'loans' | 'comex'): void {
    const field = event.field;
    const order = event.order;

    if (!field || !order || !this.data || !this.data[type]) return;

    const sorted = [...this.data[type]].sort((a, b) => {
      let valA = (a as any)[field];
      let valB = (b as any)[field];

      if (valA === undefined || valA === null) return 1;
      if (valB === undefined || valB === null) return -1;

      if (typeof valA === 'string') valA = valA.toLowerCase();
      if (typeof valB === 'string') valB = valB.toLowerCase();

      if (valA < valB) return -1 * order;
      if (valA > valB) return 1 * order;
      return 0;
    });

    (this.data as any)[type] = sorted;
  }

  handlePeriodChange(triggerFilter: boolean = true): void {
    if (this.selectedPeriod === 'custom') {
      return;
    }

    const today = startOfToday();

    switch (this.selectedPeriod) {
      case 'today':
        this.startDate = today;
        this.endDate = today;
        break;
      case 'thisWeek':
        this.startDate = startOfWeek(today, { weekStartsOn: 1 });
        this.endDate = endOfWeek(today, { weekStartsOn: 1 });
        break;
      case 'nextWeek':
        const nextWeek = addWeeks(today, 1);
        this.startDate = startOfWeek(nextWeek, { weekStartsOn: 1 });
        this.endDate = endOfWeek(nextWeek, { weekStartsOn: 1 });
        break;
      case 'thisMonth':
        this.startDate = startOfMonth(today);
        this.endDate = endOfMonth(today);
        break;
      case 'nextMonth':
        const nextMonth = addMonths(today, 1);
        this.startDate = startOfMonth(nextMonth);
        this.endDate = endOfMonth(nextMonth);
        break;
      case 'next90Days':
        this.startDate = today;
        this.endDate = addDays(today, 90);
        break;
      case 'next15Days':
        this.startDate = today;
        this.endDate = addDays(today, 15);
        break;
      case 'next7Days':
        this.startDate = today;
        this.endDate = addDays(today, 7);
        break;
    }

    if (triggerFilter) {
      this.handleFilterChange();
    }
  }

  fetchData(): void {
    this.store.dispatch(setIsLoading({ isLoading: true }));

    const filters = {
      companyId: this.selectedCompanyId || '',
      startDate: this.dateFormatter.formatDateToYYYYMMDD(this.startDate),
      endDate: this.dateFormatter.formatDateToYYYYMMDD(this.endDate),
    };

    this.consolidatedHttpService
      .getConsolidatedData(filters)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          if (response) {
            response.loans = (response.loans || []).map(l => ({
              ...l,
              estimatedAmountBRL:
                l.estimatedAmountBRL ??
                (l.currency === 'BRL' ? l.installmentAmount : l.estimatedAmountBRL),
            }));
            response.comex = (response.comex || []).map(c => ({
              ...c,
              estimatedAmountBRL:
                c.estimatedAmountBRL ?? (c.currency === 'BRL' ? c.amount : c.estimatedAmountBRL),
            }));
          }
          this.data = response;
          this.calculateKPIs();
          this.store.dispatch(setIsLoading({ isLoading: false }));
        },
        error: error => {
          this.data = null;
          this.calculateKPIs();
          this.formErrorHandlerService.showToastErrors(error);
          this.store.dispatch(setIsLoading({ isLoading: false }));
        },
      });
  }

  private calculateKPIs(): void {
    if (!this.data) {
      this.kpiGroups = [];
      return;
    }

    const groups: any[] = [];
    let grandTotalBRL = 0;

    // Group Loans
    if (this.data.loans?.length) {
      const loanCurrencies: { [curr: string]: { value: number; count: number } } = {};
      let loanTotalBRL = 0;
      this.data.loans.forEach(l => {
        if (!loanCurrencies[l.currency]) {
          loanCurrencies[l.currency] = { value: 0, count: 0 };
        }
        loanCurrencies[l.currency].value += l.installmentAmount;
        loanCurrencies[l.currency].count += 1;
        loanTotalBRL += l.estimatedAmountBRL || 0;
      });

      groups.push({
        label: 'Parcelas de Empréstimos',
        icon: 'pi pi-wallet',
        items: Object.entries(loanCurrencies)
          .filter(([curr]) => curr !== 'BRL')
          .map(([curr, data]) => ({ currency: curr, value: data.value, count: data.count })),
        totalBRL: loanTotalBRL,
      });
      grandTotalBRL += loanTotalBRL;
    }

    // Group Comex
    if (this.data.comex?.length) {
      const comexCurrencies: { [curr: string]: { value: number; count: number } } = {};
      let comexTotalBRL = 0;
      this.data.comex.forEach(c => {
        if (!comexCurrencies[c.currency]) {
          comexCurrencies[c.currency] = { value: 0, count: 0 };
        }
        comexCurrencies[c.currency].value += c.amount;
        comexCurrencies[c.currency].count += 1;
        comexTotalBRL += c.estimatedAmountBRL || 0;
      });

      groups.push({
        label: 'LCs de Importação',
        icon: 'pi pi-globe',
        items: Object.entries(comexCurrencies)
          .filter(([curr]) => curr !== 'BRL')
          .map(([curr, data]) => ({ currency: curr, value: data.value, count: data.count })),
        totalBRL: comexTotalBRL,
      });
      grandTotalBRL += comexTotalBRL;
    }

    // Derivatives contribution (Net Flow)
    let derivativeFlow = 0;
    this.data.derivatives?.forEach(d => {
      derivativeFlow += (d.netAmount || 0) - (d.incomeTax || 0);
    });
    grandTotalBRL += derivativeFlow;

    // Add Derivative as a simple group if it exists
    if (this.data.derivatives?.length) {
      groups.push({
        label: 'Ajuste Líquido Derivativos',
        icon: 'pi pi-chart-line',
        items: [],
        totalBRL: derivativeFlow,
      });
    }

    // General Total with breakdown
    const globalCurrencies: { [curr: string]: { value: number; count: number } } = {};
    this.data.loans?.forEach(l => {
      if (!globalCurrencies[l.currency]) {
        globalCurrencies[l.currency] = { value: 0, count: 0 };
      }
      globalCurrencies[l.currency].value += l.installmentAmount;
      globalCurrencies[l.currency].count += 1;
    });
    this.data.comex?.forEach(c => {
      if (!globalCurrencies[c.currency]) {
        globalCurrencies[c.currency] = { value: 0, count: 0 };
      }
      globalCurrencies[c.currency].value += c.amount;
      globalCurrencies[c.currency].count += 1;
    });

    groups.push({
      label: 'Total Geral Estimado',
      icon: 'pi pi-money-bill',
      items: Object.entries(globalCurrencies)
        .filter(([curr]) => curr !== 'BRL')
        .map(([curr, data]) => ({ currency: curr, value: data.value, count: data.count })),
      totalBRL: grandTotalBRL,
      isGeneral: true,
    });

    this.kpiGroups = groups;
  }

  exportToPdf(): void {
    const filename = `vencimentos-consoldados-${this.dateFormatter.formatDateToYYYYMMDD(new Date())}.pdf`;
    this.pdfUtilsService.exportToPdf({
      className: '.consolidated-due-date',
      filename: filename,
      title: 'Próximos Vencimentos',
      subtitle: `Período: ${this.dateFormatter.formatDateToYYYYMMDD(this.startDate)} - ${this.dateFormatter.formatDateToYYYYMMDD(this.endDate)}`,
    });
  }

  exportToExcel(): void {
    const sheets = [];

    // 1. Loans Sheet
    if (this.data?.loans?.length) {
      const rows = [];
      // Header
      rows.push(this.loanColumns.map(col => col.header));
      // Data
      this.data.loans.forEach(item => {
        rows.push(
          this.loanColumns.map(col => {
            const val = (item as any)[col.field];
            // Format if necessary, otherwise pass raw value
            return val;
          })
        );
      });
      sheets.push({ name: 'Empréstimos', rows });
    }

    // 2. Derivatives Sheet
    if (this.data?.derivatives?.length) {
      const rows = [];
      // Header
      rows.push(this.derivativeColumns.map(col => col.header));
      // Data
      this.data.derivatives.forEach(item => {
        rows.push(
          this.derivativeColumns.map(col => {
            const val = (item as any)[col.field];
            return val;
          })
        );
      });
      sheets.push({ name: 'Derivativos', rows });
    }

    // 3. Comex Sheet
    if (this.data?.comex?.length) {
      const rows = [];
      // Header
      rows.push(this.comexColumns.map(col => col.header));
      // Data
      this.data.comex.forEach(item => {
        rows.push(
          this.comexColumns.map(col => {
            const val = (item as any)[col.field];
            return val;
          })
        );
      });
      sheets.push({ name: 'Comex', rows });
    }

    if (sheets.length > 0) {
      this.tableExportService.exportMultipleSheetsToXlsx(sheets, 'vencimentos_consolidados');
    }
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
